import { useShallow } from 'zustand/react/shallow';

import { OSelect, OSwitch } from '@src/shared';

import { useMediaSettings } from '@src/hooks';
import { useRoomContext } from '@src/contexts';
import { useMediaSettingsStore } from '@src/stores';

import { PiSpeakerHigh } from 'react-icons/pi';
import { HiOutlineMicrophone } from 'react-icons/hi';

import { useNoiseCancellation } from '@src/hooks/useNoiseCancellation';
import { detectHeadphones } from '@src/helpers/mediaDevices.helpers';
import { MdGraphicEq } from 'react-icons/md';

const AudioSettings = () => {
  // Noise Cancellation Hook
  const {
    activePreset,
    setPreset,
    smartMixingEnabled,
    setSmartMixing,
    dynamicSuppressionEnabled,
    setDynamicSuppression,
    suppressionLevel,
    setSuppressionLevel,
    effectiveSuppressionLevel,
    // Individual Toggles
    preGainEnabled,
    setPreGainEnabled,
    eqEnabled,
    setEQEnabled,
    compressorEnabled,
    setCompressorEnabled,
    gateEnabled,
    setGateEnabled,
    postGainEnabled,
    setPostGainEnabled,
    // Echo Cancellation
    echoCancellationEnabled,
    setEchoCancellation,
  } = useNoiseCancellation();

  const { isMobileDevice } = useRoomContext();

  const { speakerDevices, audioInputDevices } = useMediaSettingsStore(
    useShallow((state) => ({
      speakerDevices: state.mediaSettings.speakerDevices,
      audioInputDevices: state.mediaSettings.audioInputDevices,
    }))
  );
  const { selectedAudio, selectedSpeaker, handleChangeMediaDevice } = useMediaSettings();

  const noisePresets = [
    { value: 'low', label: 'Off / Low (Browser Default)' },
    { value: 'medium', label: 'Medium (Standard AI)' },
    { value: 'high', label: 'High (DeepFilterNet v3)' },
  ];

  return (
    <div className="settings-body-container">
      <OSelect
        items={audioInputDevices.map(({ deviceId, label }) => ({ value: deviceId, label }))}
        onChange={(id) => {
          const mediaDevice = audioInputDevices.find(({ deviceId }) => deviceId === id);
          mediaDevice && handleChangeMediaDevice(mediaDevice, 'audio');
        }}
        selectedValue={selectedAudio}
        icon={<HiOutlineMicrophone size={20} color="white" />}
        classes={{
          selectContainer: 'settings-select',
        }}
        alignItems="start"
        label="Microphone"
        isDisabled={!audioInputDevices.length}
        tooltipContent={!audioInputDevices.length ? 'Allow mic access' : ''}
        size={isMobileDevice ? 'medium' : 'large'}
      />

      <OSelect
        items={speakerDevices.map(({ deviceId, label }) => ({ value: deviceId, label }))}
        onChange={(id) => {
          const mediaDevice = speakerDevices.find(({ deviceId }) => deviceId === id);
          mediaDevice && handleChangeMediaDevice(mediaDevice, 'speaker');
        }}
        alignItems="start"
        selectedValue={selectedSpeaker}
        classes={{
          selectContainer: 'settings-select',
        }}
        icon={<PiSpeakerHigh size={20} color="white" />}
        label="Speaker"
        isDisabled={!speakerDevices.length}
        tooltipContent={!speakerDevices.length ? 'Allow sound access' : ''}
        size={isMobileDevice ? 'medium' : 'large'}
      />

      {/* Noise Cancellation Control */}
      <OSelect
        items={noisePresets}
        onChange={(val) => setPreset(val as any)}
        selectedValue={activePreset}
        icon={<MdGraphicEq size={20} color="white" />}
        classes={{
          selectContainer: 'settings-select',
        }}
        alignItems="start"
        label="Noise Suppression"
        // isDisabled
        size={isMobileDevice ? 'medium' : 'large'}
      />

      {/* Smart Mixing Toggle - Only visible when noise cancellation is active */}
      {activePreset !== 'low' && (
        <>
          <div style={{ marginBottom: '16px' }}>
            <OSwitch
              label="Smart Mixing (Gate & EQ)"
              isChecked={smartMixingEnabled}
              onChange={(checked) => setSmartMixing(checked)}
              size={isMobileDevice ? 'medium' : 'large'}
            />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <OSwitch
              label="Dynamic Suppression (Auto-Adjust)"
              isChecked={dynamicSuppressionEnabled}
              isDisabled={activePreset !== 'high'}
              onChange={(checked) => setDynamicSuppression(checked)}
              size={isMobileDevice ? 'medium' : 'large'}
            />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <OSwitch
              label={`Echo Cancellation ${detectHeadphones() ? '(Headphones detected)' : '(Speakers)'}`}
              isChecked={echoCancellationEnabled}
              onChange={(checked) => setEchoCancellation(checked)}
              size={isMobileDevice ? 'medium' : 'large'}
            />
          </div>
          {activePreset === 'high' && (
            <>
              {/* Granular Smart Mixing Controls (disabled unless Smart Mixing is ON) */}
              <div
                style={{
                  marginLeft: '15px',
                  marginBottom: '10px',
                  borderLeft: '2px solid #555',
                  paddingLeft: '10px',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                }}
              >
                <div style={{ width: '48%' }}>
                  <OSwitch
                    label="Pre-Gain (1.1x)"
                    isChecked={preGainEnabled}
                    onChange={setPreGainEnabled}
                    isDisabled={!smartMixingEnabled}
                    size="medium"
                  />
                </div>
                <div style={{ width: '48%' }}>
                  <OSwitch
                    label="EQ (+3dB @ 5kHz)"
                    isChecked={eqEnabled}
                    isDisabled={!smartMixingEnabled}
                    onChange={setEQEnabled}
                    size="medium"
                  />
                </div>
                <div style={{ width: '48%' }}>
                  <OSwitch
                    label="Compressor (-12dB)"
                    isChecked={compressorEnabled}
                    onChange={setCompressorEnabled}
                    isDisabled={!smartMixingEnabled}
                    size="medium"
                  />
                </div>
                <div style={{ width: '48%' }}>
                  <OSwitch
                    label="Gate (Silence)"
                    isChecked={gateEnabled}
                    isDisabled={!smartMixingEnabled}
                    onChange={setGateEnabled}
                    size="medium"
                  />
                </div>
                <div style={{ width: '48%' }}>
                  <OSwitch
                    label="Post-Gain (1.3x)"
                    isChecked={postGainEnabled}
                    onChange={setPostGainEnabled}
                    isDisabled={!smartMixingEnabled}
                    size="medium"
                  />
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Suppression Level - Only for DeepFilter */}
      {activePreset === 'high' && (
        <div style={{ marginTop: '16px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ color: '#fff', fontSize: '14px' }}>Suppression Level</span>
            <span style={{ color: '#aaa', fontSize: '14px' }}>
              {suppressionLevel}%
              {(smartMixingEnabled || dynamicSuppressionEnabled) && effectiveSuppressionLevel !== undefined && (
                <span style={{ color: '#4caf50', marginLeft: '8px' }}>(Live: {effectiveSuppressionLevel}%)</span>
              )}
            </span>
          </div>
          <input
            type="range"
            min="0"
            max="100"
            value={suppressionLevel}
            onChange={(e) => setSuppressionLevel(Number(e.target.value))}
            style={{ width: '100%', accentColor: '#0A74E6' }}
          />
        </div>
      )}
    </div>
  );
};

export default AudioSettings;
